// Hive level 1 — the first hive level, active when the user has 0 words learned.
//
// "Tree with buzzing bee" scene: a self-contained React + requestAnimationFrame
// animation (organic curved trunk/branch silhouettes, phased foliage sway, a
// soft sun glow, drifting clouds, floating pollen, layered ground, and a bee
// with flapping wings + a fading buzz-trail).
//
// Coordinate space is the original 1600x900 canvas — the wrapper scales with
// CSS (width/height + aspect-ratio) rather than rewriting coordinates.

import React, { useEffect, useRef, useState } from 'react';

const T_DUR = 13; // seconds per loop

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

function leafCluster(cx, cy, blobs, key) {
  return (
    <g key={key}>
      {blobs.map((b, i) => (
        <ellipse
          key={i}
          cx={cx + b[0]}
          cy={cy + b[1]}
          rx={b[2]}
          ry={b[3] || b[2]}
          fill={b[4]}
        />
      ))}
    </g>
  );
}

function Tree({ t }) {
  const w = (2 * Math.PI) / T_DUR;
  const sway1 = Math.sin(w * t) * 2.4;
  const sway2 = Math.sin(w * t + 1.1) * 2.9;
  const sway3 = Math.sin(w * t + 2.3) * 2.0;

  const gDark = '#4e8f43';
  const gMid = '#67ab53';
  const gLite = '#86c46c';
  const gHi = '#a3d585';

  const mainBlobs = [
    [-110, 30, 95, 88, gDark], [100, 40, 92, 86, gDark], [0, 60, 110, 96, gDark],
    [-60, -40, 92, 86, gMid], [70, -30, 96, 90, gMid], [0, -20, 104, 96, gMid],
    [-30, -90, 80, 74, gLite], [55, -80, 76, 70, gLite], [10, -120, 70, 64, gHi],
    [-95, -30, 64, 60, gLite],
  ];
  const endBlobs = [
    [-70, 24, 70, 64, gDark], [64, 30, 66, 60, gDark], [0, 40, 80, 70, gDark],
    [-36, -18, 64, 60, gMid], [44, -22, 62, 58, gMid], [4, -8, 70, 64, gMid],
    [-10, -64, 54, 50, gLite], [40, -52, 48, 46, gHi], [-50, -44, 46, 44, gLite],
  ];
  const midBlobs = [
    [-40, 16, 50, 46, gDark], [40, 20, 48, 44, gDark], [0, 0, 56, 50, gMid],
    [-8, -34, 44, 40, gLite], [28, -26, 38, 36, gHi],
  ];

  return (
    <svg
      width="1600"
      height="900"
      viewBox="0 0 1600 900"
      style={{ position: 'absolute', inset: 0 }}
    >
      {/* trunk */}
      <path
        d="M504 818 C 504 700 512 560 528 470 C 534 436 548 412 566 398
           C 584 412 596 436 602 470 C 618 560 624 700 624 818 Z"
        fill="#8a6a4e"
      />
      <path
        d="M566 398 C 584 412 596 436 602 470 C 618 560 624 700 624 818
           L 584 818 C 582 640 576 520 566 398 Z"
        fill="#735540"
      />
      {/* root flare */}
      <path
        d="M450 821 C 494 792 524 804 566 821 C 612 802 644 794 680 821 Z"
        fill="#735540"
      />

      {/* the prominent branch — thick, sweeping up to the right */}
      <path
        d="M590 478
           C 680 458 800 430 940 392
           C 1010 372 1070 356 1118 348
           C 1078 372 1018 392 948 414
           C 820 450 700 482 596 512 Z"
        fill="#8a6a4e"
      />
      <path
        d="M590 478 C 680 458 800 430 940 392 C 1010 372 1070 356 1118 348
           C 1086 368 1040 384 986 400 C 850 438 720 472 596 500 Z"
        fill="#7a5a44"
      />
      {/* small offshoot twigs on the branch */}
      <path d="M880 410 C 892 380 904 360 928 344 C 916 366 912 388 908 410 Z" fill="#7a5a44" />
      <path d="M720 452 C 728 426 740 408 760 396 C 750 416 748 436 744 456 Z" fill="#7a5a44" />
      {/* a lower secondary branch */}
      <path d="M548 560 C 470 556 410 566 356 590 C 412 580 470 580 540 590 Z" fill="#7a5a44" />

      {/* foliage clusters (swaying) */}
      <g transform={`rotate(${sway1} 560 470)`}>
        {leafCluster(560, 250, mainBlobs, 'main')}
      </g>
      <g transform={`rotate(${sway2} 1050 430)`}>
        {leafCluster(1062, 318, endBlobs, 'end')}
      </g>
      <g transform={`rotate(${sway3} 380 600)`}>
        {leafCluster(330, 560, midBlobs, 'mid')}
      </g>
    </svg>
  );
}

function beePos(t) {
  const w = (2 * Math.PI) / T_DUR;
  const x = 980 + 250 * Math.sin(w * t) + 95 * Math.sin(2 * w * t + 1.3);
  const y = 360 + 150 * Math.cos(w * t) + 64 * Math.sin(3 * w * t + 0.6);
  // seamless buzz jitter (frequency is an integer multiple of w, so it loops cleanly)
  const jx = 3.0 * Math.sin(34 * w * t);
  const jy = 2.6 * Math.cos(40 * w * t);
  return { x: x + jx, y: y + jy };
}

function Bee({ t }) {
  const p = beePos(t);
  const pPrev = beePos(t - 0.06);
  const pNext = beePos(t + 0.06);
  const vx = pNext.x - pPrev.x;
  const vy = pNext.y - pPrev.y;
  const faceLeft = vx < 0;
  // gentle pitch toward travel direction
  let pitch = Math.atan2(vy, Math.abs(vx) + 0.001) * (180 / Math.PI);
  pitch = clamp(pitch * 0.5, -22, 22) * (faceLeft ? -1 : 1);

  // fast wing flap
  const flap = 0.35 + 0.65 * Math.abs(Math.sin(t * 52));

  // trailing buzz dots
  const trail = [];
  for (let i = 1; i <= 6; i++) {
    const tp = beePos(t - i * 0.045);
    trail.push(
      <div
        key={i}
        style={{
          position: 'absolute', left: tp.x, top: tp.y,
          width: 6 - i * 0.6, height: 6 - i * 0.6,
          marginLeft: -(3 - i * 0.3), marginTop: -(3 - i * 0.3),
          borderRadius: '50%',
          background: 'rgba(58,48,38,' + (0.18 - i * 0.025) + ')',
        }}
      />
    );
  }

  const wingFill = 'rgba(214,232,240,0.78)';
  const wingStroke = 'rgba(150,180,196,0.7)';

  return (
    <React.Fragment>
      {trail}
      <div
        style={{
          position: 'absolute', left: p.x, top: p.y,
          transform: `translate(-50%,-50%) rotate(${pitch}deg) scaleX(${faceLeft ? -1 : 1})`,
          willChange: 'transform, left, top',
        }}
      >
        <svg width="64" height="48" viewBox="-32 -24 64 48" style={{ overflow: 'visible' }}>
          <defs>
            <clipPath id="beebody">
              <ellipse cx="0" cy="0" rx="17" ry="11.5" />
            </clipPath>
          </defs>
          {/* wings (behind) */}
          <g transform={`scale(1,${flap})`} style={{ transformOrigin: '0px -6px' }}>
            <ellipse cx="-4" cy="-15" rx="9" ry="15" fill={wingFill} stroke={wingStroke} strokeWidth="1" transform="rotate(-22 -4 -8)" />
            <ellipse cx="7" cy="-16" rx="8" ry="13" fill={wingFill} stroke={wingStroke} strokeWidth="1" transform="rotate(14 7 -8)" />
          </g>
          {/* body */}
          <ellipse cx="0" cy="0" rx="17" ry="11.5" fill="#f0bf4b" />
          <g clipPath="url(#beebody)">
            <rect x="-6" y="-12" width="6" height="24" fill="#3a3026" />
            <rect x="4" y="-12" width="6" height="24" fill="#3a3026" />
            <rect x="-15" y="-12" width="4" height="24" fill="#3a3026" />
          </g>
          <ellipse cx="0" cy="0" rx="17" ry="11.5" fill="none" stroke="#caa03a" strokeWidth="1" />
          {/* head */}
          <circle cx="16" cy="-1" r="8" fill="#3a3026" />
          <circle cx="18.5" cy="-3" r="1.7" fill="#fff" opacity="0.85" />
          {/* stinger */}
          <path d="M-17 0 L-25 -2.4 L-25 2.4 Z" fill="#3a3026" />
          {/* antennae */}
          <path d="M19 -7 C 23 -13 26 -15 29 -15" stroke="#3a3026" strokeWidth="1.4" fill="none" strokeLinecap="round" />
          <circle cx="29.5" cy="-15" r="1.8" fill="#3a3026" />
        </svg>
      </div>
    </React.Fragment>
  );
}

function Pollen({ t }) {
  const w = (2 * Math.PI) / T_DUR;
  const specks = [
    [220, 300, 1.0, 0.0], [1360, 250, 0.7, 1.7], [760, 180, 0.9, 3.0],
    [1180, 520, 0.8, 4.2], [430, 470, 0.6, 2.1], [1460, 430, 0.85, 0.8],
    [150, 560, 0.7, 5.0], [640, 620, 0.6, 1.2],
  ];
  return (
    <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {specks.map((s, i) => {
        const dx = Math.sin(w * t * 1.0 + s[3]) * 26 * s[2];
        const dy = Math.cos(w * t * 1.3 + s[3]) * 20 * s[2];
        const op = 0.25 + 0.25 * (0.5 + 0.5 * Math.sin(w * t * 2 + s[3]));
        return <circle key={i} cx={s[0] + dx} cy={s[1] + dy} r={2.4 * s[2]} fill="#fdf0c0" opacity={op} />;
      })}
    </svg>
  );
}

function Scene({ t }) {
  const w = (2 * Math.PI) / T_DUR;
  const camScale = 1.015 + 0.015 * Math.sin(w * t);
  const camX = Math.sin(w * t) * 8;

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {/* sky */}
      <div
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, #bfe4ef 0%, #d4edec 46%, #eaf3df 72%, #f4efda 100%)',
        }}
      />
      {/* sun glow */}
      <div
        style={{
          position: 'absolute', left: 1140, top: 90, width: 360, height: 360,
          marginLeft: -180, marginTop: -180, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,246,210,0.95) 0%, rgba(255,246,210,0.35) 38%, rgba(255,246,210,0) 70%)',
        }}
      />
      <div
        style={{
          position: 'absolute', left: 1140, top: 90, width: 96, height: 96,
          marginLeft: -48, marginTop: -48, borderRadius: '50%',
          background: 'radial-gradient(circle, #fff7d6 0%, #ffeeb0 70%, rgba(255,238,176,0) 100%)',
        }}
      />
      {/* soft clouds */}
      <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0 }}>
        <g fill="rgba(255,255,255,0.7)" transform={`translate(${Math.sin(w * t) * 16} 0)`}>
          <ellipse cx="300" cy="160" rx="80" ry="34" />
          <ellipse cx="360" cy="150" rx="64" ry="40" />
          <ellipse cx="240" cy="172" rx="56" ry="30" />
        </g>
        <g fill="rgba(255,255,255,0.55)" transform={`translate(${Math.cos(w * t) * 22} 0)`}>
          <ellipse cx="900" cy="120" rx="70" ry="28" />
          <ellipse cx="950" cy="112" rx="54" ry="34" />
        </g>
      </svg>

      {/* scene (with gentle camera breathe) */}
      <div
        style={{
          position: 'absolute', inset: 0,
          transform: `translateX(${camX}px) scale(${camScale})`,
          transformOrigin: '60% 70%',
        }}
      >
        {/* ground hills */}
        <svg width="1600" height="900" viewBox="0 0 1600 900" style={{ position: 'absolute', inset: 0 }}>
          <path d="M0 800 C 300 760 600 770 900 790 C 1200 808 1450 790 1600 770 L1600 900 L0 900 Z" fill="#a9cf86" />
          <path d="M0 840 C 360 812 720 824 1080 838 C 1320 846 1480 838 1600 828 L1600 900 L0 900 Z" fill="#8fbf6e" />
          <ellipse cx="566" cy="816" rx="190" ry="26" fill="rgba(60,90,40,0.16)" />
        </svg>
        <Tree t={t} />
        <Pollen t={t} />
        <Bee t={t} />
      </div>

      {/* soft vignette */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          boxShadow: 'inset 0 0 220px rgba(70,90,60,0.22)',
        }}
      />
    </div>
  );
}

// Native canvas size that all child coordinates (SVGs in px, the bee's absolute
// left/top, pollen, etc.) are authored against.
const STAGE_W = 1600;
const STAGE_H = 900;

export default function HiveLevel1({ className = '', style = {} }) {
  const [t, setT] = useState(0);
  const [scale, setScale] = useState(1);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const wrapRef = useRef(null);
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (reducedMotion.current) return; // leave t at 0, static first frame
    const tick = (now) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = (now - startRef.current) / 1000;
      setT(elapsed % T_DUR);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Keep the fixed-size 1600x900 stage scaled to fit the wrapper's width.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / STAGE_W);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${STAGE_W} / ${STAGE_H}`,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <Scene t={t} />
      </div>
    </div>
  );
}
